import { createSelector } from 'reselect'
import { pathOr, propOr } from 'ramda'
import { buildingRoleLabel } from 'utils/roles'

const getBps = state => state.bpDetails

export const selectUserRelationToBp = createSelector(
  getBps,
  propOr([], 'userRelation')
)

export const getSelectedBp = createSelector(getBps, propOr([], 'selectedBp'))

export const selectHasAccessToBp = createSelector(
  getBps,
  pathOr(null, ['selectedBp', 'hasAccessToBuildingPassport'])
)

export const getBpConnectedUsers = createSelector(
  getBps,
  propOr({}, 'connectedUsers')
)

export const getOwners = createSelector(
  getBps,
  pathOr({}, ['relationship', 'owner'])
)

export const localSearchFileResults = createSelector(
  getBps,
  propOr([], 'localSearchFileResults')
)

export const localSearchTruncated = createSelector(
  getBps,
  propOr(false, 'localSearchTruncated')
)

export const guruMessages = createSelector(getBps, propOr([], 'guruMessages'))

export const getFoundUsersByRelationType = createSelector(
  getBps,
  propOr(
    {
      owner: [],
      responsiblePersons: [],
      agent: [],
      occupant: []
    },
    'relationship'
  )
)

export const getUsersAssociatedToBp = createSelector(getSelectedBp, bp => {
  const tag = (users, type) =>
    (users || []).map(user => ({
      ...user,
      relationType: type
    }))

  const allUsers = [
    ...tag(bp.agents, buildingRoleLabel('agent')),
    ...tag(bp.owners, buildingRoleLabel('owner')),
    ...tag(bp.responsiblePersons, 'Responsible'),
    ...tag(bp.occupants, 'Occupant'),
    ...tag(bp.principalAccountablePersons, 'PrincipalAccountable'),
    ...tag(bp.principalDesigners, 'PrincipalDesigner'),
    ...tag(bp.prospectivePurchasers, 'Buyer')
  ]

  const seen = new Set()
  return allUsers.filter(user => {
    if (seen.has(user.id)) return false
    seen.add(user.id)
    return true
  })
})

export const selectBulkShareStatus = createSelector(
  state => state.bpDetails,
  propOr({ loading: false, error: null }, 'bulkShareStatus')
)

export const selectExternalSharedFiles = createSelector(
  getBps,
  propOr({ loading: false, error: null, data: [] }, 'externalSharedFiles')
)

export const selectFilesSharedInternal = createSelector(
  getBps,
  propOr([], 'sharedInternals')
)

export const selectFilesSharedExternal = createSelector(
  getBps,
  propOr([], 'sharedExternals')
)

export const selectInternalSharedUsers = createSelector(
  state => state.bpDetails,
  propOr({ loading: false, error: null, data: [] }, 'internalSharedUsers')
)

export const selectBulkUnshareStatus = createSelector(
  state => state.bpDetails,
  propOr({ loading: false, error: null }, 'bulkUnshareStatus')
)
export const selectFilesSharedUsers = createSelector(
  state => state.bpDetails.filesSharedUsers || {},
  filesSharedUsers => ({
    loading: filesSharedUsers.loading || false,
    error: filesSharedUsers.error || null,
    data: {
      internalUsers:
        (filesSharedUsers.data && filesSharedUsers.data.internalUsers) || [],
      externalShares:
        (filesSharedUsers.data && filesSharedUsers.data.externalShares) || []
    }
  })
)
export const selectUserFoundByEmail = createSelector(
  state => state.bpDetails,
  propOr(null, 'userFoundByEmail')
)

export const selectCurrentBpHasTaskManagement = createSelector(
  getSelectedBp,
  bp => bp?.hasTaskManagement ?? bp?.has_task_management ?? false
)

export const selectIsSearchLoading = createSelector(
  getBps,
  propOr(false, 'isSearching')
)
